import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import compensationService from './compensationService';

import type {
    CompensationComputedView,
    CompensationCycle,
    CompensationCycleBudgetRequest,
    CompensationCycleOverview,
    CompensationDraftRequest,
    CompensationEnvelope,
    CompensationEnvelopeCreateRequest,
    CompensationSalaryParseResult,
    CompensationSaveRequest,
    CompensationVersion,
} from '@/types/compensation';

import { queryClient } from '@/AppWithProviders';
import { CompensationMutationKey, CompensationQueryKey } from '@/constants/QueryKeys';

const invalidateCycleOverview = (cycleEvaluationId: string) =>
    queryClient.invalidateQueries({
        queryKey: [CompensationQueryKey.CYCLE_OVERVIEW, cycleEvaluationId],
    });

const invalidateEnvelope = (envelopeId: string) =>
    queryClient.invalidateQueries({ queryKey: [CompensationQueryKey.ENVELOPE, envelopeId] });

export const useGetCompensationCycles = () => {
    return useQuery<CompensationCycle[]>({
        queryKey: [CompensationQueryKey.CYCLES],
        queryFn: () => compensationService.getCycles(),
    });
};

export const useGetCompensationCycleOverview = (cycleEvaluationId: string | undefined) => {
    return useQuery<CompensationCycleOverview>({
        queryKey: [CompensationQueryKey.CYCLE_OVERVIEW, cycleEvaluationId],
        // `enabled` guards this call: it never runs while no cycle is selected yet.
        queryFn: () => compensationService.getCycleOverview(cycleEvaluationId as string),
        enabled: !!cycleEvaluationId,
    });
};

export const useSetCompensationCycleBudget = () => {
    return useMutation<void, Error, { cycleId: string; request: CompensationCycleBudgetRequest }>({
        mutationFn: ({ cycleId, request }) => compensationService.setCycleBudget(cycleId, request),
        onSuccess: (_data, { cycleId }) => invalidateCycleOverview(cycleId),
    });
};

export const useGetCompensationEnvelope = (id: string | undefined) => {
    return useQuery<CompensationEnvelope>({
        queryKey: [CompensationQueryKey.ENVELOPE, id],
        queryFn: () => compensationService.getEnvelope(id as string),
        enabled: !!id,
    });
};

export interface UsePreviewCompensationEnvelopeQueryParams {
    envelopeId: string | undefined;
    /** Already debounced by the caller; nothing is previewed without one. */
    draft: CompensationDraftRequest | undefined;
}

export const usePreviewCompensationEnvelopeQuery = ({
    envelopeId,
    draft,
}: UsePreviewCompensationEnvelopeQueryParams) => {
    return useQuery<CompensationComputedView>({
        queryKey: [CompensationQueryKey.PREVIEW, envelopeId, draft],
        queryFn: () =>
            compensationService.previewEnvelope(
                envelopeId as string,
                draft as CompensationDraftRequest,
            ),
        enabled: !!envelopeId && !!draft,
        // Every draft is its own key: an abandoned one is dropped at once rather than cached.
        gcTime: 0,
        // Keeps the previous totals on screen while a debounced keystroke's preview refetches,
        // instead of flashing the adjustments table empty on every edit.
        placeholderData: keepPreviousData,
    });
};

export const useCreateCompensationEnvelope = () => {
    return useMutation<CompensationEnvelope, Error, CompensationEnvelopeCreateRequest>({
        mutationFn: (request) => compensationService.createEnvelope(request),
        onSuccess: (envelope) => invalidateCycleOverview(envelope.cycleEvaluationId),
    });
};

export const useSaveCompensationEnvelope = () => {
    return useMutation<
        CompensationEnvelope,
        Error,
        { id: string; request: CompensationSaveRequest }
    >({
        mutationKey: [CompensationMutationKey.SAVE],
        mutationFn: ({ id, request }) => compensationService.saveEnvelope(id, request),
        onSuccess: (envelope, { id }) => {
            invalidateEnvelope(id);
            invalidateCycleOverview(envelope.cycleEvaluationId);
        },
    });
};

export const useDeleteCompensationEnvelope = () => {
    return useMutation<void, Error, { id: string; cycleEvaluationId: string }>({
        mutationFn: ({ id }) => compensationService.deleteEnvelope(id),
        onSuccess: (_data, { cycleEvaluationId }) => invalidateCycleOverview(cycleEvaluationId),
    });
};

export const useCloseCompensationEnvelope = () => {
    return useMutation<CompensationEnvelope, Error, string>({
        mutationFn: (id) => compensationService.closeEnvelope(id),
        onSuccess: (envelope) => {
            invalidateEnvelope(envelope.id);
            invalidateCycleOverview(envelope.cycleEvaluationId);
        },
    });
};

export const useReopenCompensationEnvelope = () => {
    return useMutation<CompensationEnvelope, Error, string>({
        mutationFn: (id) => compensationService.reopenEnvelope(id),
        onSuccess: (envelope) => {
            invalidateEnvelope(envelope.id);
            invalidateCycleOverview(envelope.cycleEvaluationId);
        },
    });
};

export const useGetCompensationEnvelopeVersions = (id: string | undefined) => {
    return useQuery<CompensationVersion[]>({
        queryKey: [CompensationQueryKey.VERSIONS, id],
        queryFn: () => compensationService.getVersions(id as string),
        enabled: !!id,
    });
};

export const useRestoreCompensationEnvelopeVersion = () => {
    return useMutation<CompensationEnvelope, Error, { id: string; versionId: string }>({
        mutationFn: ({ id, versionId }) => compensationService.restoreVersion(id, versionId),
        onSuccess: (envelope, { id }) => {
            invalidateEnvelope(id);
            invalidateCycleOverview(envelope.cycleEvaluationId);
            queryClient.invalidateQueries({ queryKey: [CompensationQueryKey.VERSIONS, id] });
        },
    });
};

// A GET under the hood, but modeled as a mutation: it only ever runs on demand from a click
// (never on mount, never cached/refetched), and the button needs the per-call `isPending`/error
// state a query's shared cache entry doesn't give an on-demand download.
export const useExportCompensationEnvelope = () => {
    return useMutation<Blob, Error, string>({
        mutationKey: [CompensationMutationKey.EXPORT],
        mutationFn: (id) => compensationService.exportEnvelope(id),
    });
};

export const useParseCompensationSalaries = () => {
    return useMutation<CompensationSalaryParseResult, Error, { id: string; content: string }>({
        mutationKey: [CompensationMutationKey.PARSE_SALARIES],
        mutationFn: ({ id, content }) => compensationService.parseSalaries(id, content),
    });
};
